import { Card, CardContent, Typography } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import { t } from 'i18next';

import { PricingSourceType } from 'pricing/enums';
import { PricingHelper } from 'pricing/helpers';
import { useCostDecimals } from 'pricing/hooks';
import { ICustomerQuotePart } from 'quotes/types';
import { Color, Level, Variant } from 'theme/enums';

interface IProps {
  quotePart: ICustomerQuotePart;
  label?: string;
  displayPurchaseCost?: boolean;
  sx?: SxProps;
}

const getCardPropsStyle = (oldPrice: boolean) => {
  if (oldPrice) {
    return { color: Color.DANGER, variant: Variant.SOFT };
  }

  return { color: Color.NEUTRAL, variant: Variant.PLAIN };
};

const getCostTextPropsStyle = (oldPrice: boolean) => {
  if (oldPrice) {
    return { color: Color.DANGER };
  }

  return {};
};

const getCostLabel = (costSourceType: PricingSourceType) => {
  switch (costSourceType) {
    case PricingSourceType.CORPORATE:
      return t('labelStandardCost');
    case PricingSourceType.FAMILY:
      return t('labelFamilyCost');
    case PricingSourceType.CUSTOM:
      return t('labelEnteredCost');
    case PricingSourceType.SELECTED_SPA:
      return t('labelSelectedSpaCost');
    case PricingSourceType.DEFAULT:
      return t('labelCustomerCost');
    case PricingSourceType.PROJECT:
      return t('labelProjectCost');
    case PricingSourceType.VENDOR:
      return t('labelVendorCost');
    default:
      return t('labelCost');
  }
};

export const CostCard = ({
  quotePart,
  label = null,
  displayPurchaseCost = true,
  sx = null,
}: IProps) => {
  const costDecimals = useCostDecimals();

  const hasExpiredCost =
    PricingSourceType.CORPORATE === quotePart.computedCostSourceType &&
    !PricingHelper.isCostValidByStatus(quotePart?.computedCostingStatus);

  const costValue = PricingHelper.formatPricing({
    amount: quotePart.computedCost,
    decimals: costDecimals,
    displayZero: false,
    fixedDecimals: true,
  });

  const purchaseCostValue = displayPurchaseCost
    ? PricingHelper.formatPricing({
        amount: quotePart.purchaseCost,
        decimals: costDecimals,
        displayZero: false,
        fixedDecimals: true,
      })
    : null;

  return (
    <Card {...getCardPropsStyle(hasExpiredCost)} sx={{ width: '100%', ...sx }}>
      <CardContent>
        <Typography color={Color.NEUTRAL} level={Level.BODY_XS} pb={1}>
          {label || getCostLabel(quotePart.computedCostSourceType)}
        </Typography>
        <Typography {...getCostTextPropsStyle(hasExpiredCost)} level={Level.TITLE_LG}>
          {costValue}
        </Typography>
        {purchaseCostValue && (
          <Typography color={Color.NEUTRAL} level={Level.BODY_XS}>
            {t('labelPurchaseCostWithValue', { value: purchaseCostValue })}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
